// Yönetim panelinin bilgi mimarisi (v9).
//
// Menü tek, sade bir kenar menüdür: üstte başlıksız Ana sayfa, Talepler,
// Mesajlar ve Yapılacaklar; altında kullanıcının düşündüğü varlığa göre
// (Maçlar, Ligler, Takımlar, Oyuncular…) açılıp kapanan bölümler; kişisel
// araçlar en altta Araçlarım'da. Bölümlerin alt başlığı yoktur; her sayfa tek
// bir bölümdedir.
//
// permissions alanı olan öğeler, kullanıcının o yetkilerden en az birine sahip
// olmadığı durumda gizlenir; roles alanı olanlar yalnızca o rollere görünür.
// Yetkiler admin panelindeki "Kullanıcı yetkilendirme" ekranından verilir.
// keywords: komut paletinde (Ctrl/⌘ K) eş anlamlı aramalar için.
// formerly: sayfanın eski adı ve eski menü başlıkları; eski düzene alışmış
// kullanıcı "Saha ve Planlama", "Lig Yönetimi" gibi adlarla da bulur.
import { hasAnyPermission } from "../utils/roleManagement";
import { LEAGUE_SETTINGS_PERMISSIONS } from "../features/leagueSettings/permissions";
import { DESKTOP_NAV_GROUP_KEYS, isDesktopApp } from "../utils/desktopApp";

export const DASHBOARD_PATH = "/editor";
export const ACCOUNT_PATH = "/yonetim/hesabim";
export const TODOS_PATH = "/yonetim/yapilacaklar";
export const TOOLS_PATH = "/yonetim/araclar";
export const MESSAGES_PATH = `${TOOLS_PATH}/mesajlar`;
export const REQUESTS_NAV_PATH = "/yonetim/talepler";
// Muhasebe merkezi: Hesaplar ve Virman, Ön Muhasebe, Maç Muhasebesi, Türkiye Geneli.
export const ACCOUNTING_PATH = `${TOOLS_PATH}/muhasebe`;
export const accountingSectionPath = (section) => `${ACCOUNTING_PATH}?bolum=${section}`;

// Eski rol merkezleri: tek Kontrol Paneli'ne yönlenir (App.js).
export const LEGACY_HUB_PATHS = ["/yonetim/admin", "/yonetim/editor", "/yonetim/il-yoneticisi", "/yonetim/lig-yoneticisi", "/yonetim/mac-yoneticisi"];

// Araçlar: her yönetim kullanıcısının kişisel çalışma araçları. Yetki
// gerekmez; kayıtlar yalnız sahibine görünür. Araçlar Merkezi bu listeyi
// kart olarak gösterir (Yapılacaklar ve Mesajlar dahil).
export const TOOL_ITEMS = [
  { key: "hub", name: "Araçlar Merkezi", path: TOOLS_PATH, icon: "toolbox", description: "Tüm araçlar ve bugünün özeti tek ekranda." },
  { key: "projects", name: "Projeler", path: `${TOOLS_PATH}/projeler`, icon: "project", description: "Süreç adımları ve sorumlular, soru-cevap, aranan kişiler, görüşme notları ve portföy tek yerde." },
  { key: "todos", name: "Yapılacaklar Listesi", path: TODOS_PATH, icon: "checklist", badge: "todos", description: "Görevler, hatırlatmalar, maç görevleri ve pano görünümü." },
  { key: "agenda", name: "Ajanda ve Takvim", path: `${TOOLS_PATH}/ajanda`, icon: "calendar", description: "Ay, hafta ve gün görünümü; tekrar eden etkinlikler ve hatırlatmalar." },
  { key: "notes", name: "Not Defteri", path: `${TOOLS_PATH}/not-defteri`, icon: "notebook", description: "Renkli notlar, işaretlenebilir listeler, etiket ve arşiv." },
  { key: "accounting", name: "Muhasebe", path: `${TOOLS_PATH}/muhasebe`, icon: "receipt", description: "Hesaplar ve virman, ön muhasebe, maç muhasebesi ve Türkiye geneli tek yerde." },
  { key: "sheets", name: "Tablolar", path: `${TOOLS_PATH}/tablolar`, icon: "sheet", description: "Formüllü hesap tabloları: TOPLA, EĞER, DÜŞEYARA… çoklu sayfa ve şablonlar." },
  { key: "messages", name: "Mesajlar", path: MESSAGES_PATH, icon: "message", description: "WhatsApp gibi sohbet: yöneticiler, takım başkanları, oyuncular ve üyelerle birebir yazışma ve gruplar; üyelerin ElitLig Yönetimi yazışmaları da burada." },
  { key: "contacts", name: "Rehber", path: `${TOOLS_PATH}/rehber`, icon: "contact", description: "Takımlar ve üyeler: takım adı öne çıkar; başkanlar, kadro, maçlar, tek dokunuşla ara, yaz, mesaj gönder." },
  { key: "calc", name: "Hesap Araçları", path: `${TOOLS_PATH}/hesap-araclari`, icon: "calculator", description: "KDV, yüzde, iskonto, aidat bölüştürme ve tarih hesapları." },
  { key: "focus", name: "Odak Sayacı", path: `${TOOLS_PATH}/odak-sayaci`, icon: "timer", description: "Pomodoro, geri sayım ve kronometre; maç süresi için de uygun." },
];

// İş akışında yer alan araçlar Araçlarım bölümünde tekrar edilmez.
const WORKFLOW_TOOL_KEYS = ["todos", "messages"];

const TOOL_KEYWORDS = {
  hub: "araçlar özet",
  projects: "süreç proje satış görüşme",
  agenda: "takvim etkinlik randevu",
  notes: "not liste",
  accounting: "gelir gider kasa virman ön muhasebe maç muhasebesi",
  sheets: "excel hesap tablosu",
  contacts: "telefon kişi başkan iletişim",
  calc: "kdv yüzde iskonto aidat",
  focus: "pomodoro kronometre geri sayım",
};

// Araçlarım'daki kısa (cümle düzeninde) adlar; Araçlar Merkezi kartları TOOL_ITEMS adlarını kullanır.
const TOOL_NAV_NAMES = {
  hub: "Araçlar merkezi",
  agenda: "Ajanda",
  notes: "Not defteri",
  calc: "Hesap araçları",
  focus: "Odak sayacı",
};

// Eski menü başlıkları (v7 ve v8): arama eski adla gelen kullanıcıyı da bulur.
const OLD = {
  ligYonetimi: "Lig Yönetimi",
  ligSezon: "Lig ve Sezon",
  sahaPlanlama: "Saha ve Planlama",
  macMerkezi: "Maç Merkezi",
  takimOyuncu: "Takımlar ve Oyuncular",
  kurallarDisiplin: "Kurallar ve Disiplin",
  icerikOdul: "İçerik ve Ödüller",
  icerikMedya: "İçerik ve Medya",
  finans: "Finans ve Piyasa",
  ticari: "Ticari ve Finans",
  kullanicilarSistem: "Kullanıcılar ve Sistem",
  uyelerYetkiler: "Üyeler ve Yetkiler",
  siteSistem: "Site ve Sistem",
};

export const NAV_GROUPS = [
  // Üstte başlıksız: Ana sayfa (tek, role duyarlı Kontrol Paneli).
  {
    key: "genel",
    label: "Ana sayfa",
    icon: "home",
    top: true,
    items: [
      { name: "Ana sayfa", path: DASHBOARD_PATH, icon: "home", desc: "Bugünün maçları, talepler, görevler ve kısayollar.", keywords: "kontrol paneli genel bakış yönetim merkezi özet dashboard", formerly: "Kontrol Paneli Genel Bakış Yönetim Merkezi", aliases: LEGACY_HUB_PATHS, aliasExact: true },
    ],
  },
  // Üstte başlıksız: iş akışı — rozetli.
  {
    key: "is-akisi",
    label: "İş akışı",
    icon: "inbox",
    top: true,
    workflow: true,
    items: [
      // Talepler (onay merkezi): rozet toplam açık talep sayısıdır; ilgili
      // menü öğelerinde kategori sayısı da görünür (features/requests).
      { name: "Talepler", path: REQUESTS_NAV_PATH, icon: "inbox", badge: "requests", desc: "Onay bekleyen profil, transfer, kadro ve disiplin talepleri.", keywords: "onay bekleyen başvuru istek" },
      { name: "Mesajlar", path: MESSAGES_PATH, icon: "message", badge: "messages", desc: "Yönetim, takım ve üye yazışmaları.", keywords: "sohbet chat whatsapp yazışma" },
      { name: "Yapılacaklar", path: TODOS_PATH, icon: "checklist", badge: "todos", desc: "Görevlerin, hatırlatmaların ve maç görevlerin.", keywords: "görev todo hatırlatma iş listesi" },
    ],
  },
  {
    key: "maclar",
    label: "Maçlar",
    icon: "ball",
    desc: "Maç listesi, saha planlama ve maç personeli.",
    items: [
      // Maç oluşturma ve eski maç adresleri de "Maç listesi" altında sayılır.
      { name: "Maç listesi", path: "/editor/maclar", icon: "ball", permissions: ["matches.view", "matches.create", "matches.update"], keywords: "maçlar fikstür maç programı skor sonuç kadro canlı reji", formerly: `Maçlar ${OLD.ligYonetimi} ${OLD.macMerkezi}`, aliases: ["/editor/mac-olustur", "/editor/mac-guncelle", "/editor/ist-gir", "/editor/mac-ekle", "/editor/mac-paneli-beta", "/editor/canli-mac"] },
      { name: "Toplu düzenleme", path: "/editor/maclar/toplu-duzenle", icon: "pen", permissions: ["matches.bulk_edit"], keywords: "maçları toplu düzenle saat saha değiştir", formerly: `Maçları Toplu Düzenle ${OLD.ligYonetimi} ${OLD.macMerkezi}` },
      { name: "Saha ve maç planlama", path: "/editor/sahalar", icon: "pin", permissions: ["venues.view", "venues.manage"], keywords: "saha saat havuz eşleştirme program", formerly: `${OLD.sahaPlanlama} ${OLD.macMerkezi}` },
      { name: "Takım müsaitliği", path: "/editor/musaitlik", icon: "clock", permissions: ["venues.view"], keywords: "müsaitlik tablosu uygun gün saat", formerly: `Takım Müsaitlik Tablosu ${OLD.sahaPlanlama} ${OLD.macMerkezi}` },
      { name: "Personel", path: "/yonetim/admin/personel", icon: "badge", permissions: ["staff.view"], keywords: "hakem spiker reji fotoğrafçı sağlıkçı", formerly: `${OLD.sahaPlanlama} ${OLD.macMerkezi}` },
      { name: "Toplu personel atama", path: "/yonetim/admin/personel/toplu-atama", icon: "users", permissions: ["staff.assign"], keywords: "görevlendirme atama hakem ata", formerly: `${OLD.sahaPlanlama} ${OLD.macMerkezi}` },
      { name: "Maç değerlendirmeleri", path: "/yonetim/mac-degerlendirmeleri", icon: "clipboard", permissions: ["evaluations.view", "evaluations.submit", "evaluations.manage"], keywords: "performans form puan bonus kesinti", formerly: `${OLD.sahaPlanlama} ${OLD.macMerkezi}` },
    ],
  },
  {
    key: "ligler",
    label: "Ligler",
    icon: "trophy",
    desc: "Lig yapısı, sezonlar, puan durumu ve kurallar.",
    items: [
      { name: "Ligler", path: "/editor/ligler", icon: "trophy", permissions: ["leagues.create", "leagues.update", "leagues.delete", "leagues.archive"], keywords: "lig ekle arşiv", formerly: `${OLD.ligYonetimi} ${OLD.ligSezon}` },
      { name: "Sezonlar", path: "/editor/sezonlar", icon: "calendar", permissions: ["seasons.create", "seasons.update", "seasons.delete", "seasons.archive", "seasons.rule_set"], keywords: "sezon takvim dönem", formerly: `${OLD.ligYonetimi} ${OLD.ligSezon}` },
      // Gruplar, Lig Ayarlamaları → Lig Statüsü bölümündedir.
      { name: "Lig statüsü ve gruplar", path: "/editor/lig-ayarlari?bolum=lig-statusu", icon: "layers", permissions: ["groups.view", "groups.create", "groups.update", "groups.delete"], keywords: "grup eleme tur statü", formerly: `Lig Statüsü ${OLD.ligYonetimi} ${OLD.ligSezon}` },
      { name: "Puan durumu", path: "/editor/puan-durumu", icon: "standings", permissions: ["standings.update"], keywords: "tablo sıralama puan", formerly: `${OLD.ligYonetimi} ${OLD.ligSezon}` },
      { name: "Kura çekimi", path: "/yonetim/kura", icon: "shuffle", permissions: ["draws.view", "draws.run"], keywords: "kura torba eşleşme", formerly: `${OLD.ligYonetimi} ${OLD.ligSezon}` },
      // Kural kitapçıkları; puanlama sistemi ve kadro kuralları da kitapçıktan yönetilir.
      // Eski adresler (/editor/kurallar, /editor/kadro-kural-setleri) çalışmaya devam eder.
      { name: "Lig ayarları ve kurallar", path: "/editor/lig-ayarlari", icon: "book", permissions: LEAGUE_SETTINGS_PERMISSIONS, keywords: "kural kitapçığı puanlama kadro kuralı ayar", formerly: `Lig Ayarlamaları ${OLD.kurallarDisiplin} ${OLD.ligSezon}`, aliases: ["/editor/kurallar"] },
      { name: "Junior Cup", path: "/yonetim/junior-cup", icon: "star", permissions: ["league_portals.view", "league_portals.manage"], keywords: "lig portalı turnuva portal", formerly: `${OLD.icerikOdul} ${OLD.ligSezon}` },
    ],
  },
  {
    key: "takimlar",
    label: "Takımlar",
    icon: "shield",
    desc: "Takım kayıtları, kadrolar ve takım finansı.",
    items: [
      { name: "Takımlar", path: "/editor/takimlar", icon: "shield", permissions: ["teams.create", "teams.update", "teams.delete"], keywords: "takım kulüp ekle logo", formerly: `${OLD.ligYonetimi} ${OLD.takimOyuncu}` },
      { name: "Takım rehberi", path: "/editor/takim-rehberi", icon: "book", permissions: ["teams.directory"], keywords: "başkan iletişim telefon yetkili", formerly: `${OLD.sahaPlanlama} ${OLD.takimOyuncu}` },
      { name: "Kadro listeleri", path: "/editor/kadro-listeleri", icon: "checklist", permissions: ["roster_rules.requests"], keywords: "tüm oyuncularım liste", formerly: `${OLD.kurallarDisiplin} ${OLD.takimOyuncu}` },
      { name: "Kadro onayları", path: "/editor/kadro-onaylari", icon: "check", permissions: ["roster_rules.requests"], keywords: "sezon kadrosu taslak onay", formerly: `${OLD.kurallarDisiplin} ${OLD.takimOyuncu}` },
      { name: "Takım finansı ve FFP", path: "/yonetim/takim-finans", icon: "coins", permissions: ["team_finance.view", "team_finance.manage"], keywords: "ffp bütçe finans takım", formerly: `Takım Finansı & FFP Finans ${OLD.finans} ${OLD.ticari}` },
      { name: "Takım birleştirme", path: "/editor/takim-birlestirme", icon: "layers", permissions: ["teams.merge"], keywords: "mükerrer kopya takım birleştir", formerly: `${OLD.ligYonetimi} ${OLD.takimOyuncu}` },
    ],
  },
  {
    key: "oyuncular",
    label: "Oyuncular",
    icon: "users",
    desc: "Oyuncu kayıtları, transferler ve piyasa değeri.",
    items: [
      { name: "Oyuncular", path: "/editor/oyuncular", icon: "users", permissions: ["players.create", "players.update", "players.delete"], keywords: "oyuncu futbolcu profil", formerly: `${OLD.ligYonetimi} ${OLD.takimOyuncu}` },
      { name: "Transfer onayları", path: "/yonetim/admin/transfer-onaylari", icon: "swap", permissions: ["transfers.view", "transfers.review"], keywords: "transfer teklif onay", formerly: `${OLD.kurallarDisiplin} ${OLD.takimOyuncu}` },
      { name: "Piyasa değeri yönetimi", path: "/yonetim/piyasa-degeri", icon: "chart", permissions: ["market_value.view", "market_value.recalculate", "market_value.settings"], keywords: "etl değer formül hesapla", formerly: `Finans ${OLD.finans} ${OLD.ticari}` },
      { name: "Piyasa değeri sıralaması", path: "/piyasa-degeri", icon: "trending", permissions: ["market_value.view"], keywords: "etl sıralama en değerli", formerly: `Finans ${OLD.finans} ${OLD.ticari}` },
      { name: "Oyuncu birleştirme", path: "/editor/oyuncu-birlestirme", icon: "layers", permissions: ["players.merge"], keywords: "mükerrer kopya oyuncu birleştir", formerly: `${OLD.ligYonetimi} ${OLD.takimOyuncu}` },
    ],
  },
  {
    key: "disiplin",
    label: "Disiplin",
    icon: "gavel",
    desc: "Cezalar, savunmalar ve itirazlar.",
    items: [
      { name: "Cezalar", path: "/editor/cezalar", icon: "gavel", permissions: ["penalties.view", "penalties.create"], keywords: "disiplin ceza kart men sevk", formerly: `Cezalar (Disiplin) ${OLD.kurallarDisiplin}` },
      { name: "Savunma ve itirazlar", path: "/yonetim/admin/savunmalar", icon: "message", permissions: ["penalties.review"], keywords: "savunma itiraz cas kurul", formerly: OLD.kurallarDisiplin },
    ],
  },
  {
    key: "icerik",
    label: "İçerik",
    icon: "news",
    desc: "Haberler, görseller, ödüller, yayın ve sponsorlar.",
    items: [
      { name: "Haberler", path: "/editor/haberler", icon: "news", permissions: ["news.create", "news.update"], keywords: "haber duyuru yayınla", formerly: `${OLD.icerikOdul} ${OLD.icerikMedya}` },
      { name: "Yazı ekle", path: "/editor/yazi-ekle", icon: "pen", permissions: ["news.writings"], keywords: "yazı köşe makale", formerly: `${OLD.icerikOdul} ${OLD.icerikMedya}` },
      { name: "İçerik havuzu", path: "/icerik-havuzu", icon: "image", permissions: ["content.pool"], keywords: "görsel şablon sosyal medya paylaşım", formerly: `${OLD.icerikOdul} ${OLD.icerikMedya}` },
      { name: "Haftanın enleri", path: "/yonetim/haftanin-enleri", icon: "star", permissions: ["weekly_awards.view", "weekly_awards.update"], keywords: "ödül haftanın oyuncusu golcü", formerly: `${OLD.icerikOdul} ${OLD.icerikMedya}`, aliases: ["/admin/weekly-awards"] },
      { name: "Elitlig Panorama", path: "/yonetim/panorama", icon: "tv", permissions: ["panorama.view", "panorama.broadcast"], keywords: "yayın reji ekran panorama", formerly: `${OLD.icerikOdul} ${OLD.icerikMedya}` },
      { name: "Sponsorlar", path: "/yonetim/sponsorlar", icon: "handshake", permissions: ["sponsors.view", "sponsors.manage"], keywords: "sponsor reklam led kaplama", formerly: `${OLD.icerikOdul} ${OLD.ticari}` },
      // Eskiden menüde olmayan sayfa; kendi yetkisiyle görünür.
      { name: "Final tahmin yönetimi", path: "/final-tahmin-yonetim", icon: "trending", permissions: ["content.final_tahmin"], keywords: "tahmin oran final oyun", formerly: OLD.icerikMedya },
    ],
  },
  {
    key: "uyeler",
    label: "Üyeler",
    icon: "userCog",
    desc: "Site üyeleri, premium üyelik ve şifre talepleri.",
    items: [
      // "Üye yönetimi" site üyelerini (oyuncu, takım başkanı) listeler; admin,
      // editör gibi yetkili hesaplar "Yönetim ve ayarlar"daki Yönetim kullanıcılarındadır.
      { name: "Üye yönetimi", path: "/yonetim/admin/kullanicilar", icon: "users", permissions: ["users.view"], keywords: "üye kullanıcı kayıt hesap oyuncu başkan", formerly: `${OLD.kullanicilarSistem} ${OLD.uyelerYetkiler}`, aliases: ["/editor/uye-yonetimi"] },
      { name: "Premium onayları", path: "/editor/premium-onaylari", icon: "crown", permissions: ["users.membership"], keywords: "premium üyelik sipariş ödeme", formerly: `${OLD.kullanicilarSistem} ${OLD.ticari}` },
      { name: "Şifre sıfırlama talepleri", path: "/yonetim/admin/sifre-talepleri", icon: "lock", permissions: ["users.password_reset"], keywords: "şifremi unuttum parola kod", formerly: `${OLD.kullanicilarSistem} ${OLD.uyelerYetkiler}` },
    ],
  },
  {
    key: "yonetim",
    label: "Yönetim ve ayarlar",
    short: "Yönetim",
    icon: "key",
    desc: "Yönetim hesapları, yetkiler, şehirler ve sistem kayıtları.",
    items: [
      { name: "Yönetim kullanıcıları", path: "/yonetim/admin/yonetim-kullanicilari", icon: "userPlus", permissions: ["management_users.view"], keywords: "admin editör il yöneticisi hesap aç", formerly: `${OLD.kullanicilarSistem} ${OLD.uyelerYetkiler}`, aliases: ["/editor/kullanici-ekle"] },
      { name: "Kullanıcı yetkilendirme", path: "/yonetim/admin/yetkilendirme", icon: "key", permissions: ["permissions.view", "permissions.manage"], keywords: "yetki rol izin şablon", formerly: `${OLD.kullanicilarSistem} ${OLD.uyelerYetkiler}` },
      { name: "Şehirler", path: "/yonetim/admin/sehirler", icon: "building", permissions: ["city_pages.manage"], keywords: "il şehir ekle harita", formerly: `${OLD.kullanicilarSistem} ${OLD.siteSistem}` },
      { name: "Şehir iletişim sayfaları", path: "/yonetim/admin/iletisim", icon: "phone", permissions: ["city_pages.contacts"], keywords: "iletişim sosyal medya whatsapp", formerly: `${OLD.kullanicilarSistem} ${OLD.siteSistem}` },
      { name: "Ziyaretçi analizi", path: "/yonetim/admin/ziyaretci-analizi", icon: "chart", permissions: ["analytics.view"], keywords: "analitik trafik ziyaretçi istatistik", formerly: `${OLD.kullanicilarSistem} ${OLD.siteSistem}` },
      { name: "İşlem kayıtları", path: "/yonetim/admin/islem-kayitlari", icon: "clipboard", permissions: ["activity_logs.view"], keywords: "log kayıt denetim geçmiş", formerly: `${OLD.kullanicilarSistem} ${OLD.siteSistem}` },
      { name: "Çöp kutusu", path: "/yonetim/admin/cop-kutusu", icon: "trash", permissions: ["trash.view"], keywords: "silinen geri al", formerly: `${OLD.kullanicilarSistem} ${OLD.siteSistem}` },
    ],
  },
  {
    key: "araclar",
    label: "Araçlarım",
    icon: "toolbox",
    tools: true,
    desc: "Kişisel çalışma araçların.",
    // Muhasebe (Hesaplar, Ön Muhasebe, Maç Muhasebesi, Türkiye Geneli) burada,
    // her yönetim kullanıcısına açık; eski Finans başlığıyla da bulunur.
    items: TOOL_ITEMS.filter((tool) => !WORKFLOW_TOOL_KEYS.includes(tool.key)).map((tool) => ({
      name: TOOL_NAV_NAMES[tool.key] || tool.name,
      path: tool.path,
      icon: tool.icon,
      desc: tool.description,
      keywords: TOOL_KEYWORDS[tool.key] || "",
      formerly: tool.key === "accounting" ? `Araçlar Finans ${OLD.finans} ${OLD.ticari}` : `Araçlar ${tool.name}`,
    })),
  },
];

/** Üstte başlıksız duran gruplar (Ana sayfa, iş akışı) ve açılır bölümler. */
export const isTopGroup = (group) => Boolean(group?.top);

// Komut paletindeki işlemler (Ctrl/⌘ K). Yetkiye göre süzülür.
export const COMMAND_ACTIONS = [
  { key: "new-match", name: "Yeni maç oluştur", path: "/editor/mac-olustur", icon: "plus", permissions: ["matches.create"], keywords: "maç ekle fikstür" },
  { key: "new-news", name: "Haber yayınla", path: "/editor/haberler", icon: "news", permissions: ["news.create"], keywords: "yeni haber duyuru" },
  { key: "new-writing", name: "Yeni yazı ekle", path: "/editor/yazi-ekle", icon: "pen", permissions: ["news.writings"], keywords: "makale köşe" },
  { key: "new-penalty", name: "Yeni ceza gir", path: "/editor/cezalar/yeni", icon: "gavel", permissions: ["penalties.create"], keywords: "disiplin ceza ekle" },
  { key: "bulk-staff", name: "Maçlara personel ata", path: "/yonetim/admin/personel/toplu-atama", icon: "users", permissions: ["staff.assign"], keywords: "hakem spiker görevlendir" },
  { key: "run-draw", name: "Kura çek", path: "/yonetim/kura", icon: "shuffle", permissions: ["draws.run"], keywords: "kura torba" },
  { key: "new-todo", name: "Yeni görev ekle", path: TODOS_PATH, icon: "checklist", keywords: "yapılacak hatırlatma todo" },
  { key: "open-requests", name: "Bekleyen talepleri aç", path: `${REQUESTS_NAV_PATH}?durum=open`, icon: "inbox", keywords: "onay talep" },
  { key: "account", name: "Hesabım", path: ACCOUNT_PATH, icon: "user", keywords: "profil şifre güvenlik" },
  { key: "site", name: "Siteyi görüntüle", path: "/", icon: "external", keywords: "site ana sayfa elitlig" },
];

export const canSeeNavItem = (user, item) =>
  (!item.roles || item.roles.includes(user?.role)) &&
  (!item.excludeRoles || !item.excludeRoles.includes(user?.role)) &&
  (!item.permissions || hasAnyPermission(user, item.permissions)) &&
  (!item.hideWithPermissions || !hasAnyPermission(user, item.hideWithPermissions));

/** Türkçe harfleri sadeleştirir: "Çöp" → "cop", "MAÇLAR" → "maclar". */
export const foldText = (value = "") =>
  String(value)
    .toLocaleLowerCase("tr-TR")
    .replace(/ı/g, "i")
    .replace(/ş/g, "s")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "");

// "ı" ve "i" aynı sayılır: eski bağlantılarda "/editor/maçlar" gibi yazımlar var.
const normalizePath = (value = "") => value.replace(/ı/g, "i").replace(/\/+$/, "") || "/";
const splitPath = (value = "") => {
  const [pathname, query = ""] = String(value).split("?");
  return { pathname: normalizePath(pathname), query };
};

/** Aranan metin: ad, bölüm adı, eş anlamlılar, eski ad ve eski menü başlıkları. */
const itemHaystack = (item, group) => foldText(`${item.name} ${group.label} ${item.keywords || ""} ${item.formerly || ""}`);

const itemMatchesText = (item, group, words) => {
  if (!words.length) return true;
  const hay = itemHaystack(item, group);
  return words.every((word) => hay.includes(word));
};

const queryWords = (query) => foldText(query).split(/\s+/).filter(Boolean);

/** Yetkiye göre süzülmüş gruplar; boş kalan grup listeden düşer. */
export function visibleNavGroups(user, query = "", { desktop = isDesktopApp() } = {}) {
  const words = queryWords(query);
  // Mac masaüstü uygulaması yalnız iş akışı ve Araçlarım'ı gösterir (utils/desktopApp.js).
  const groups = desktop ? NAV_GROUPS.filter((group) => DESKTOP_NAV_GROUP_KEYS.includes(group.key)) : NAV_GROUPS;
  return groups.map((group) => {
    const items = group.items
      .filter((item) => canSeeNavItem(user, item))
      .filter((item) => itemMatchesText(item, group, words));
    return { ...group, items };
  }).filter((group) => group.items.length > 0);
}

/** Görünür tüm sayfalar düz liste: [{ item, group }]. */
export const flattenNav = (groups) => groups.flatMap((group) => group.items.map((item) => ({ item, group })));

/** Yola karşılık gelen menü öğesi (sık kullanılanlar / son ziyaretler için). */
export function navEntryForPath(path, groups) {
  const target = normalizePath(String(path || ""));
  return flattenNav(groups).find(({ item }) => normalizePath(item.path) === target) || null;
}

/**
 * Komut paleti araması: sayfalar ve işlemler. Ad başta eşleşirse öne çıkar;
 * eş anlamlılar (keywords), bölüm adı ve eski adlar (formerly) da aranır.
 * Türkçe harflere duyarsız.
 */
export function searchCommands(user, query, { groups = visibleNavGroups(user), limit = 30 } = {}) {
  const words = queryWords(query);
  const score = (name, hay) => {
    if (!words.length) return 1;
    if (!words.every((word) => hay.includes(word))) return 0;
    const folded = foldText(name);
    if (folded.startsWith(words.join(" "))) return 4;
    if (words.every((word) => folded.includes(word))) return 3;
    return 1;
  };
  const pages = flattenNav(groups).map(({ item, group }) => ({
    type: "page",
    id: `page:${item.path}`,
    name: item.name,
    path: item.path,
    icon: item.icon,
    section: group.label,
    score: score(item.name, itemHaystack(item, group)),
  }));
  const actions = COMMAND_ACTIONS.filter((action) => canSeeNavItem(user, action)).map((action) => ({
    type: "action",
    id: `action:${action.key}`,
    name: action.name,
    path: action.path,
    icon: action.icon,
    section: "İşlem",
    score: score(action.name, foldText(`${action.name} ${action.keywords || ""}`)),
  }));
  const all = [...actions, ...pages].filter((entry) => entry.score > 0);
  if (!words.length) return all.slice(0, limit);
  return all.sort((a, b) => b.score - a.score).slice(0, limit);
}

/**
 * Açık sayfaya en iyi uyan menü öğesi. Tam eşleşme yoksa en uzun ön ek
 * kazanır: "/editor/maclar/12/canli" sayfasında "Maçlar" seçili görünür.
 * Sorgulu öğeler ("?bolum=lig-statusu") yalnızca sorgu da uyduğunda seçilir.
 */
export function findActiveNav(pathname, groups, search = "") {
  const path = normalizePath(pathname);
  const params = new URLSearchParams(search || "");
  let best = null;
  let bestScore = -1;
  for (const group of groups) {
    for (const item of group.items) {
      const target = splitPath(item.path);
      if (target.query) {
        const wanted = new URLSearchParams(target.query);
        const ok = [...wanted.entries()].every(([key, value]) => params.get(key) === value);
        if (!ok) continue;
      }
      const exact = path === target.pathname;
      const prefix = !exact && target.pathname !== DASHBOARD_PATH && target.pathname !== "/yonetim" && path.startsWith(`${target.pathname}/`);
      const alias = !exact && !prefix && (item.aliases || []).some((a) => path === a || (!item.aliasExact && path.startsWith(`${a}/`)));
      if (!exact && !prefix && !alias) continue;
      // Sorgu uyan öğe aynı yolun sorgusuz öğesinin önüne geçer.
      const score = (alias ? 0 : target.pathname.length * 2) + (target.query ? 1 : 0) + (exact ? 1000 : 0);
      if (score > bestScore) { best = { group, item }; bestScore = score; }
    }
  }
  return best;
}
